// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.

import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { ConfigurationService } from '../../services/configuration.service';
import { AuthService } from '../../services/auth.service';
import { TenantService } from '../../services/tenant.service';
import { Observable, of } from 'rxjs';
import { map, catchError, take, switchMap } from 'rxjs/operators';

export interface MenuAuthConfig {
    menuKey: string;
}

@Injectable({
    providedIn: 'root'
})
export class MenuAuthGuard implements CanActivate {
    constructor(
        private configurationService: ConfigurationService,
        private authService: AuthService,
        private tenantService: TenantService,
        private router: Router
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> {
        // Get the menuKey from route data
        const menuKey = route.data['menuKey'] as string;

        if (!menuKey) {
            console.error('MenuAuthGuard: menuKey not provided in route data');
            return of(false);
        }

        return this.configurationService.getConfiguration().pipe(
            switchMap((config) => {
                // Get menu configuration for the specified key
                const menuItems = config?.['menu'] ?? [];
                const menuConfig = menuItems.find((item: any) => item.menu === menuKey);

                if (!menuConfig) {
                    console.warn(`MenuAuthGuard: Menu configuration not found for key: ${menuKey}`);
                    return of(false);
                }

                // Check if menu is active
                if (!menuConfig.isActive) {
                    console.warn(`MenuAuthGuard: Menu ${menuKey} is not active`);
                    return of(false);
                }

                // Check authType - if not 'public', require login
                const authType = menuConfig.authType;
                const authTypeStr = String(authType).trim().toLowerCase();

                // Public menus don't require authentication
                if (authTypeStr === 'public') {
                    return of(true);
                }

                // Non-public menus require authentication - check auth state properly
                return this.authService.authState.pipe(
                    take(1),
                    switchMap((authState) => {
                        if (!authState.isAuthenticated) {
                            // Redirect to login with return URL and menuKey for PIN verification
                            this.router.navigate(
                                [`${this.tenantService.getTenantKey()}/login`],
                                { queryParams: { returnUrl: state.url, menuKey: menuKey } }
                            );
                            return of(false);
                        }

                        // For PIN-required menus (JwtAndPin), user MUST have verified PIN
                        // Even admins need to verify PIN for PIN-protected menus
                        if (authTypeStr === 'jwtandpin') {
                            const user = authState.user;
                            if (!user) {
                                // No user data - redirect to login
                                this.router.navigate(
                                    [`${this.tenantService.getTenantKey()}/login`],
                                    { queryParams: { returnUrl: state.url, menuKey: menuKey } }
                                );
                                return of(false);
                            }

                            // Check if menu is in authorizedMenus (from PIN verification)
                            const normalizedMenuKey = menuKey?.toString().toLowerCase();
                            const hasPinAccess = user.authorizedMenus?.includes(menuKey) || 
                                                user.authorizedMenus?.some(m => m?.toLowerCase() === normalizedMenuKey);

                            if (!hasPinAccess) {
                                console.log(`MenuAuthGuard: Menu ${menuKey} requires PIN verification`);
                                // Redirect to login to prompt for PIN
                                this.router.navigate(
                                    [`${this.tenantService.getTenantKey()}/login`],
                                    { queryParams: { returnUrl: state.url, menuKey: menuKey } }
                                );
                                return of(false);
                            }
                        } else {
                            // For non-PIN menus (JwtOnly), check if user has access
                            const normalizedMenuKey = menuKey?.toString().toLowerCase();
                            const hasMenuAccess = this.authService.hasAccessToMenu(menuKey) || 
                                                this.authService.hasAccessToMenu(normalizedMenuKey);

                            if (!hasMenuAccess) {
                                console.warn(`MenuAuthGuard: User does not have access to ${menuKey}`);
                                this.router.navigate(
                                    [`${this.tenantService.getTenantKey()}/login`],
                                    { queryParams: { returnUrl: state.url, menuKey: menuKey } }
                                );
                                return of(false);
                            }
                        }

                        return of(true);
                    })
                );
            }),
            catchError((error) => {
                console.error(`MenuAuthGuard: Error checking menu configuration for ${menuKey}:`, error);
                return of(false);
            })
        );
    }
}
